import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight, CalendarDays } from "lucide-react";

const forOptions = ["Myself", "My children", "My grandchildren", "My family", "Someone else"];
const rhythmOptions = [
  { key: "daily", label: "Every day", note: "A gentle question waiting each day" },
  { key: "twice_weekly", label: "A few times a week", note: "A comfortable, steady pace" },
  { key: "weekly", label: "Once a week", note: "One question to sit with each week" },
  { key: "none", label: "Not right now", note: "No reminders — I'll come when I can" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    preferred_name: "", birth_year: "", birth_place: "", grew_up_place: "", memoir_for: "Myself",
    reminder_frequency: "twice_weekly",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding", {
        ...form,
        birth_year: form.birth_year ? parseInt(form.birth_year, 10) : null,
      });
      await refreshUser();
      navigate("/home");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const steps = [
    { key: "preferred_name", label: "What would you like us to call you?", placeholder: "e.g. Margaret", type: "text" },
    { key: "birth_year", label: "What year were you born?", placeholder: "e.g. 1958", type: "number" },
    { key: "birth_place", label: "Where were you born?", placeholder: "e.g. Brighton, England", type: "text" },
    { key: "grew_up_place", label: "Where did you grow up?", placeholder: "e.g. a small town by the sea", type: "text" },
  ];

  const forStep = steps.length;       // 4
  const rhythmStep = steps.length + 1; // 5
  const isLast = step === steps.length + 2; // 6
  const totalDots = steps.length + 3; // 7
  const canContinue = step === 0 ? form.preferred_name.trim() : true;

  return (
    <div className="min-h-screen grain bg-cream flex items-center justify-center p-6 relative">
      <div className="w-full max-w-lg relative z-10">
        <img src="/logo.png" alt="Write My Story" className="h-24 w-24 object-contain mx-auto mb-6" />
        <div className="bg-white rounded-3xl border border-line p-8 sm:p-10 shadow-sm animate-fade-up">
          {/* progress dots */}
          <div className="flex gap-1.5 mb-8">
            {Array.from({ length: totalDots }).map((_, i) => (
              <div key={i} className={`h-1.5 rounded-full flex-1 transition-colors ${i <= step ? "bg-coral" : "bg-line"}`} />
            ))}
          </div>

          {step < steps.length && (
            <div key={step} className="animate-fade-up">
              <h2 className="font-serif text-2xl sm:text-3xl text-charcoal">{steps[step].label}</h2>
              <input
                data-testid={`onboard-${steps[step].key}`}
                type={steps[step].type} autoFocus value={form[steps[step].key]}
                onChange={(e) => set(steps[step].key, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && canContinue) setStep(step + 1); }}
                placeholder={steps[step].placeholder}
                className="mt-6 w-full h-14 px-5 text-lg rounded-xl border border-line bg-cream focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none"
              />
            </div>
          )}

          {step === forStep && (
            <div className="animate-fade-up">
              <h2 className="font-serif text-2xl sm:text-3xl text-charcoal">Who are you mainly creating this memoir for?</h2>
              <div className="mt-6 space-y-2.5">
                {forOptions.map((o) => (
                  <button
                    key={o} data-testid={`onboard-for-${o.replace(/\s/g, "-").toLowerCase()}`}
                    onClick={() => set("memoir_for", o)}
                    className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                      form.memoir_for === o ? "border-teal bg-sagelight text-teal font-semibold" : "border-line bg-white hover:border-sage"
                    }`}
                  >{o}</button>
                ))}
              </div>
            </div>
          )}

          {step === rhythmStep && (
            <div className="animate-fade-up">
              <div className="flex items-center gap-2 text-sage font-semibold text-sm"><CalendarDays size={16} /> Your rhythm</div>
              <h2 className="font-serif text-2xl sm:text-3xl text-charcoal mt-2">How often should we nudge you to write?</h2>
              <p className="text-subtle mt-2">A little and often works best. We'll send you a question to answer — you can change this any time in Settings.</p>
              <div className="mt-6 space-y-2.5">
                {rhythmOptions.map((o) => (
                  <button
                    key={o.key} data-testid={`onboard-rhythm-${o.key}`}
                    onClick={() => set("reminder_frequency", o.key)}
                    className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                      form.reminder_frequency === o.key ? "border-teal bg-sagelight text-teal" : "border-line bg-white hover:border-sage"
                    }`}
                  >
                    <span className="font-semibold block">{o.label}</span>
                    <span className="text-sm text-subtle">{o.note}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {isLast && (
            <div className="animate-fade-up text-center">
              <h2 className="font-serif text-2xl sm:text-3xl text-charcoal">You don't need to be a writer.</h2>
              <p className="mt-4 text-lg text-subtle leading-relaxed">
                Just tell us what happened. We'll ask the questions and help you turn your memories
                into your story — one at a time.
              </p>
              <button
                data-testid="onboard-start-btn" onClick={finish} disabled={busy}
                className="mt-8 w-full h-14 rounded-xl bg-coral hover:bg-coraldark text-white font-semibold text-lg transition-colors flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="animate-spin" /> : <>Start my story <ArrowRight size={20} /></>}
              </button>
            </div>
          )}

          {!isLast && (
            <div className="mt-8 flex items-center justify-between">
              <button
                data-testid="onboard-back-btn"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className={`text-subtle hover:text-charcoal ${step === 0 ? "invisible" : ""}`}
              >Back</button>
              <button
                data-testid="onboard-next-btn"
                onClick={() => canContinue && setStep(step + 1)} disabled={!canContinue}
                className="flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-7 py-3 rounded-full font-semibold transition-colors disabled:opacity-40"
              >Continue <ArrowRight size={18} /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
